import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { CrmEvent, Patient, PatientLtvSecure, VissmedClientSnapshotRow } from '../lib/types';
import { formatCents, formatDate, formatDateTime, formatPhone } from '../lib/format';
import { LgpdApi } from '../lib/crmApi';
import { humanizeCrmError, humanizePatientListError } from '../lib/apiErrors';
import { useAuth } from '../auth/AuthContext';

export default function PacienteDetail() {
  const { id } = useParams<{ id: string }>();
  const { role } = useAuth();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [events, setEvents] = useState<CrmEvent[]>([]);
  const [ltv, setLtv] = useState<PatientLtvSecure | null>(null);
  const [vissmed, setVissmed] = useState<VissmedClientSnapshotRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorHint, setErrorHint] = useState<string | null>(null);
  const [forgetting, setForgetting] = useState(false);
  const [forgetMsg, setForgetMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setErrorHint(null);
    (async () => {
      const [pRes, eRes, lRes, vRes] = await Promise.all([
        supabase.from('patients').select('*').eq('id', id).maybeSingle(),
        supabase
          .from('events')
          .select('*')
          .eq('patient_id', id)
          .order('occurred_at', { ascending: false })
          .limit(100),
        supabase.from('patient_ltv_secure').select('*').eq('patient_id', id).maybeSingle(),
        supabase.from('vissmed_client_snapshot').select('*').eq('patient_id', id).maybeSingle(),
      ]);
      if (pRes.error) {
        const { message, hint } = humanizePatientListError(pRes.error.message);
        setError(message);
        setErrorHint(hint ?? null);
      }
      setPatient((pRes.data as Patient | null) ?? null);
      setEvents((eRes.data ?? []) as CrmEvent[]);
      setLtv((lRes.data as PatientLtvSecure | null) ?? null);
      setVissmed((vRes.data as VissmedClientSnapshotRow | null) ?? null);
      setLoading(false);
    })();
  }, [id]);

  async function handleForget() {
    if (!patient) return;
    if (!confirm(`Apagar dados do paciente ${patient.name ?? patient.phone_e164}? Esta ação é irreversível (LGPD).`)) return;
    setForgetting(true);
    const r = await LgpdApi.forget(patient.id);
    setForgetting(false);
    setForgetMsg(
      r.ok
        ? `Paciente marcado pra esquecer. Purga definitiva em ${r.data?.scheduled_purge_in_days ?? 30} dias (LGPD).`
        : humanizeCrmError(r.error, r.status),
    );
  }

  if (loading) return <div className="text-slate-500">Carregando…</div>;
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm max-w-xl">
        <div>{error}</div>
        {errorHint && <div className="mt-2 text-xs text-red-600/90">{errorHint}</div>}
        <Link to="/pacientes" className="inline-block mt-3 text-brand-700 hover:underline text-sm">← Voltar aos pacientes</Link>
      </div>
    );
  }
  if (!patient) return <div className="text-slate-500">Paciente não encontrado. <Link to="/pacientes" className="text-brand-700 hover:underline">Voltar</Link></div>;

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link to="/pacientes" className="text-sm text-slate-500 hover:text-slate-700">← Pacientes</Link>
          <h1 className="text-2xl font-semibold mt-1">{patient.name ?? '(sem nome)'}</h1>
          <div className="text-sm text-slate-600 mt-1">{formatPhone(patient.phone_e164)}{patient.email && ` · ${patient.email}`}</div>
        </div>
        {role === 'admin' && (
          <button
            onClick={handleForget}
            disabled={forgetting}
            className="text-sm px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
          >{forgetting ? 'Apagando…' : 'LGPD: esquecer'}</button>
        )}
      </div>

      {forgetMsg && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-sm mb-4">
          {forgetMsg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card label="Estágio (CRM)" value={patient.lifecycle_stage ?? '—'} />
        <Card label="Origem" value={patient.first_seen_source ?? '—'} />
        <Card
          label="LTV (agendamentos CRM)"
          value={formatCents(ltv?.total_value_cents)}
          sub={`${ltv?.appointments_count ?? 0} consultas no CRM`}
        />
      </div>

      {vissmed && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card
            label="LTV total (Vissmed)"
            value={formatCents(vissmed.total_spent_cents)}
            sub="Histórico agregado no relatório"
          />
          <Card
            label="Ticket médio"
            value={formatCents(
              vissmed.total_visits && vissmed.total_spent_cents != null
                ? Math.round(vissmed.total_spent_cents / vissmed.total_visits)
                : null,
            )}
            sub={`${vissmed.total_visits ?? 0} atendimentos (Vissmed)`}
          />
          <Card label="Última especialidade" value={vissmed.last_specialty ?? '—'} />
          <Card label="1ª consulta (Vissmed)" value={formatDate(vissmed.first_visit_at)} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card label="Primeiro registro no CRM" value={formatDate(patient.first_seen_at)} />
        <Card
          label="Última visita (Vissmed)"
          value={vissmed?.last_visit_at ? formatDate(vissmed.last_visit_at) : '—'}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="font-medium">Timeline ({events.length})</h2>
        </div>
        <ul className="divide-y divide-slate-100">
          {events.length === 0 && (
            <li className="px-4 py-6 text-center text-slate-400 text-sm">Nenhum evento ainda.</li>
          )}
          {events.map((e) => (
            <li key={e.id} className="px-4 py-3 flex items-start gap-3 text-sm">
              <EventDot type={e.event_type} />
              <div className="flex-1">
                <div className="font-medium text-slate-800">{labelEvent(e.event_type)}</div>
                <div className="text-xs text-slate-500">
                  {formatDateTime(e.occurred_at)}
                  {e.channel && ` · ${e.channel}`}
                  {e.source_system && ` · ${e.source_system}`}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function EventDot({ type }: { type: string }) {
  const colors: Record<string, string> = {
    whatsapp_inbound: 'bg-emerald-500',
    whatsapp_outbound: 'bg-emerald-300',
    menu_click: 'bg-blue-500',
    pixel_view: 'bg-slate-400',
    pixel_click: 'bg-blue-400',
    call_inbound: 'bg-amber-500',
    call_outbound: 'bg-amber-300',
    appointment_scheduled: 'bg-violet-500',
    appointment_attended: 'bg-violet-700',
    hot_lead_transfer: 'bg-rose-500',
    lgpd_forget: 'bg-red-700',
  };
  return <span className={`mt-1.5 inline-block w-2 h-2 rounded-full ${colors[type] ?? 'bg-slate-400'}`} />;
}

function labelEvent(type: string) {
  const m: Record<string, string> = {
    whatsapp_inbound: 'WhatsApp recebida',
    whatsapp_outbound: 'WhatsApp enviada',
    menu_click: 'Menu clicado',
    pixel_view: 'Visita ao site',
    pixel_click: 'Clique tracked',
    call_inbound: 'Ligação recebida',
    call_outbound: 'Ligação feita',
    appointment_scheduled: 'Consulta agendada',
    appointment_attended: 'Consulta atendida',
    hot_lead_transfer: 'Transferência hot lead',
    lgpd_forget: 'Solicitação LGPD',
  };
  return m[type] ?? type;
}
